/**
 * 결재경로 팝업 (그룹웨어).
 *
 * 문서기안(GrwDraftPopup)의 "결재라인추가"에서 띄운다.
 *
 *   왼쪽      : 부서 목록
 *   오른쪽 위 : 부서/검색으로 찾은 사원 목록
 *   오른쪽 아래: 결재 · 합의 · 공람 · 수신처 · 시행자 탭별 결재경로
 *
 * 사원 목록에서 행을 더블클릭하면 지금 열린 탭에 담기고,
 * 아래 표에서 더블클릭하면 빠진다.
 *
 * 조회 · 저장은 화면에서 넘긴 콜백으로 위임한다 — 이 팝업은 API를 부르지 않는다.
 * 다른 브랜치와 이름이 겹치지 않도록 Grw 접두어를 쓴다.
 */
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { type ColumnConfig } from "@shared/components/GridV2/SnGridTypes";
import { showDialog } from "@widgets/CommonDialog";
import { StyleSettings } from "../../styles/StyleSettings";

/** 부서 한 건 */
export interface GrwDeptRow {
  cd_dept: string;
  nm_dept: string;
}

/** 사원 한 건 */
export interface GrwEmpRow {
  cd_dept?: string;
  nm_dept: string;
  cd_emp?: string;
  nm_emp: string;
  /** 직급 코드 (mas_emp.fg_pos — 공통코드 HRS007) */
  fg_pos?: string;
  /** 직급명 (HRS007 조인 결과). 없으면 코드가 그대로 쓰인다 */
  nm_pos?: string;
}

/** 결재경로 한 줄 — 어느 탭에 담겼는지(tp_line)와 순서를 갖는다 */
export interface GrwLineRow extends GrwEmpRow {
  /** 결재 / 합의 / 공람 / 수신처 / 시행자 */
  tp_line: string;
  /** 결재순서 */
  no_order: number;
}

/**
 * 경로그룹 한 건 (결재라인 템플릿).
 *
 * 매번 사람을 하나씩 담지 않아도 되도록, 담아둔 결재경로를 이름 하나로 묶어둔 것.
 * code = 템플릿 번호, lines = 그때 담겨 있던 결재경로.
 */
export interface GrwPathGroup {
  code: string;
  name: string;
  lines?: GrwLineRow[];
}

/** 탭 구성 기본값 — 화면에서 tabs로 덮어쓸 수 있다 */
const DEFAULT_TABS = ["결재", "합의", "공람", "수신처", "시행자"];

/**
 * ColumnConfig는 DB에서 읽은 sys_formgridcolumn 행을 전제로 cd_firm/cd_menu/cd_user를
 * 필수로 요구하는데, 이 목록은 코드로 만든 것이라 해당 없다.
 */
const COL_BASE = { cd_firm: "", cd_menu: "", cd_user: "", cd_grid: "grid1" };

/** 사원 목록 컬럼 (No 열은 SnGrid가 자동으로 붙인다) */
const EMP_COLUMNS: ColumnConfig[] = [
  {
    ...COL_BASE,
    cd_column: "nm_dept",
    nm_column: "부서명",
    tp_column: "SnTextBox",
    qt_width: 160,
    fg_visible: "Y",
    no_seq: 1,
  },
  {
    ...COL_BASE,
    cd_column: "nm_emp",
    nm_column: "사원명",
    tp_column: "SnTextBox",
    qt_width: 140,
    fg_visible: "Y",
    no_seq: 2,
  },
  {
    // 직급명(HRS007 조인 결과). 쿼리가 안 내려주면 빈칸으로 보여 원인이 바로 드러난다.
    ...COL_BASE,
    cd_column: "nm_pos",
    nm_column: "직급",
    tp_column: "SnTextBox",
    qt_width: 90,
    fg_visible: "Y",
    no_seq: 3,
  },
];

/**
 * 읽기전용으로 두는 조건.
 *
 * SnTextBox는 dc_editcon을 계산해 그 결과의 반대를 editable로 쓴다(SnColumnsCreate).
 * 항상 참인 식을 주면 언제나 편집 불가가 된다. 사람 정보는 사원 목록에서 담긴
 * 값이라 여기서 고칠 게 아니고, 편집이 열려 있으면 더블클릭이 "빼기"와 겹친다.
 */
const READONLY_CON = "1 == 1";

/** 결재경로 컬럼 — 결재순서만 편집 가능하게 둔다 */
const LINE_COLUMNS: ColumnConfig[] = [
  {
    ...COL_BASE,
    cd_grid: "grid2",
    cd_column: "no_order",
    nm_column: "결재순서",
    tp_column: "SnNumberBox",
    qt_width: 90,
    fg_visible: "Y",
    no_seq: 1,
  },
  {
    ...COL_BASE,
    cd_grid: "grid2",
    cd_column: "nm_dept",
    nm_column: "부서명",
    tp_column: "SnTextBox",
    qt_width: 160,
    fg_visible: "Y",
    no_seq: 2,
    dc_editcon: READONLY_CON,
  },
  {
    ...COL_BASE,
    cd_grid: "grid2",
    cd_column: "nm_emp",
    nm_column: "사원명",
    tp_column: "SnTextBox",
    qt_width: 140,
    fg_visible: "Y",
    no_seq: 3,
    dc_editcon: READONLY_CON,
  },
  {
    ...COL_BASE,
    cd_grid: "grid2",
    cd_column: "nm_pos",
    nm_column: "직급",
    tp_column: "SnTextBox",
    qt_width: 90,
    fg_visible: "Y",
    no_seq: 4,
    dc_editcon: READONLY_CON,
  },
];

/** 같은 사람인지 가리는 키 — 코드가 있으면 코드로, 없으면 이름으로 */
const keyOf = (r: {
  cd_emp?: string;
  nm_emp?: string;
  cd_dept?: string;
  nm_dept?: string;
}) => `${r.cd_emp || r.nm_emp || ""}|${r.cd_dept || r.nm_dept || ""}`;

interface Props {
  /**
   * 지울 수도 순서를 바꿀 수도 없는 고정 행 (기안자).
   * tp_line이 가리키는 탭의 맨 앞에 항상 1번으로 놓인다.
   */
  fixedLine?: GrwLineRow;
  /** 왼쪽 부서 목록 */
  depts?: GrwDeptRow[];
  /** 경로그룹 콤보 목록 (onLoadPathGroups를 안 넘길 때의 고정 목록) */
  pathGroups?: GrwPathGroup[];
  /** 이미 담겨 있던 결재경로 (다시 열 때) */
  defaultValue?: GrwLineRow[];
  /**
   * 표시할 탭 목록. 넘기지 않으면 5개 전부 나온다.
   * 화면마다 쓰는 구분이 달라 목록을 밖에서 정할 수 있게 열어둔다.
   */
  tabs?: string[];
  /** 사원 조회 — 부서를 고르거나 검색을 누르면 호출된다 */
  onSearchEmp?: (cond: {
    cd_dept?: string;
    nm_emp?: string;
  }) => Promise<GrwEmpRow[]> | GrwEmpRow[];
  /**
   * 경로그룹 목록 조회.
   *
   * 팝업은 showPopupModal로 한 번 그려진 뒤 화면의 state 변화를 못 받는다(children이 그때의
   * 엘리먼트로 굳는다). 그래서 목록을 props로 받지 않고 이 콜백으로 직접 읽어, 추가·삭제 뒤에도
   * 다시 읽어 콤보를 갱신한다.
   */
  onLoadPathGroups?: () => Promise<GrwPathGroup[]> | GrwPathGroup[];
  /** 경로그룹 추가. false를 돌려주면 실패로 보고 입력값·목록을 그대로 둔다 */
  onAddPathGroup?: (
    nm_group: string,
    lines: GrwLineRow[]
  ) => Promise<boolean | void> | boolean | void;
  /** 경로그룹 삭제. false를 돌려주면 실패(또는 취소)로 보고 목록을 그대로 둔다 */
  onDeletePathGroup?: (
    cd_group: string
  ) => Promise<boolean | void> | boolean | void;
}

/**
 * 템플릿(js_line)에 담을 값만 남긴다.
 * 표에서 읽은 행에는 그리드가 붙인 내부 필드가 섞여 있어 그대로 저장하면 같이 들어간다.
 */
const toTemplateLine = (l: GrwLineRow): GrwLineRow => ({
  cd_dept: l.cd_dept,
  nm_dept: l.nm_dept,
  cd_emp: l.cd_emp,
  nm_emp: l.nm_emp,
  fg_pos: l.fg_pos,
  nm_pos: l.nm_pos,
  tp_line: l.tp_line,
  no_order: Number(l.no_order) || 0,
});

const GrwApprovalLinePopup = forwardRef((props: Props, ref) => {
  const {
    fixedLine,
    depts = [],
    pathGroups = [],
    defaultValue = [],
    tabs,
    onSearchEmp,
    onLoadPathGroups,
    onAddPathGroup,
    onDeletePathGroup,
  } = props;

  // 빈 배열을 넘기면 탭이 하나도 없어 표를 못 그리므로 기본값으로 되돌린다.
  const tabList = tabs && tabs.length > 0 ? tabs : DEFAULT_TABS;

  /** 고정 행(기안자)인지 — 같은 탭·같은 사람이면 고정 행이다 */
  const isFixed = (r: GrwLineRow) =>
    !!fixedLine && r.tp_line === fixedLine.tp_line && keyOf(r) === keyOf(fixedLine);

  /** 고정 행이 빠져 있으면 되살린다. 이미 있으면 하나만 남긴다 */
  const withFixed = (list: GrwLineRow[]): GrwLineRow[] =>
    fixedLine
      ? [{ ...fixedLine, no_order: 1 }, ...list.filter((l) => !isFixed(l))]
      : list;

  /**
   * 탭마다 결재순서 숫자대로 줄을 세운다. 번호는 손대지 않는다.
   *
   * 고정 행은 숫자와 무관하게 언제나 맨 앞이다.
   * 같은 번호끼리는 담긴 순서를 지킨다 (Array.sort는 안정 정렬).
   */
  const sortByOrder = (list: GrwLineRow[]): GrwLineRow[] => {
    const byTab = new Map<string, GrwLineRow[]>();
    list.forEach((l) => {
      const arr = byTab.get(l.tp_line) ?? [];
      arr.push(l);
      byTab.set(l.tp_line, arr);
    });

    const out: GrwLineRow[] = [];
    byTab.forEach((arr) => {
      arr
        .sort((a, b) => {
          if (isFixed(a) !== isFixed(b)) return isFixed(a) ? -1 : 1;
          return (Number(a.no_order) || 0) - (Number(b.no_order) || 0);
        })
        .forEach((l) => out.push(l));
    });
    return out;
  };

  /**
   * 줄을 세운 뒤 1번부터 번호를 다시 매긴다.
   *
   * 순서칸에 3, 2를 적어 넣으면 그 숫자대로 정렬된 뒤 1,2,3으로 정리된다.
   * 편집 중에는 부르지 않는다 — 방금 친 숫자가 바로 덮여 되돌아가 보인다.
   */
  const renumber = (list: GrwLineRow[]): GrwLineRow[] =>
    sortByOrder(list).map((l, i, all) => {
      // 탭이 바뀌는 지점마다 1번으로 되돌린다
      const seq = all.slice(0, i).filter((p) => p.tp_line === l.tp_line).length;
      return { ...l, no_order: seq + 1 };
    });

  /** 고정 행을 보장하고 줄만 세운다 (편집 중) */
  const arrange = (list: GrwLineRow[]) => sortByOrder(withFixed(list));

  /** 고정 행을 보장하고 순번까지 정리한다 (담기·빼기·확인) */
  const finalize = (list: GrwLineRow[]) => renumber(withFixed(list));

  const empGridRef = useRef<any>(null);
  const lineGridRef = useRef<any>(null);

  /**
   * 그리드 팝업(콤보 목록 등)이 그려질 자리.
   * 지정하지 않으면 SnGrid가 <body>로 떨어지는데, 모달 밖이라 focus trap에 막혀
   * 목록이 뜨자마자 닫힌다(SnGrid.tsx의 popupParent 폴백).
   */
  const rootRef = useRef<HTMLDivElement>(null);
  const [popupParentEl, setPopupParentEl] = useState<HTMLElement | null>(null);

  const [cdDept, setCdDept] = useState("");
  const [empKeyword, setEmpKeyword] = useState("");
  const [groupName, setGroupName] = useState("");
  const [cdGroup, setCdGroup] = useState("");
  const [empRows, setEmpRows] = useState<GrwEmpRow[]>([]);
  const [tab, setTab] = useState<string>(tabList[0]);
  const [lines, setLines] = useState<GrwLineRow[]>(() => finalize(defaultValue));
  const [groups, setGroups] = useState<GrwPathGroup[]>(pathGroups);

  useEffect(() => {
    setPopupParentEl(rootRef.current);
  }, []);

  useImperativeHandle(ref, () => ({
    // 확인 시 담긴 결재경로 전체를 돌려준다 (탭 구분은 tp_line에 들어 있다)
    getSelectedValue: () => {
      lineGridRef.current?.stopEditing?.();
      return { approvalLines: readLines() };
    },
  }));

  /**
   * 표에서 편집한 값(결재순서)을 전체 목록에 반영한 결과.
   *
   * 어느 탭 소속인지는 lines가 기준이다. 표에 남아 있는 행을 그대로 지금 탭
   * 소속으로 삼으면, 탭을 옮긴 직후 표가 아직 이전 탭 내용을 들고 있는 순간에
   * 그 사람들이 통째로 새 탭에 복제된다. 그래서 표에서는 값만 가져오고
   * 탭 소속은 건드리지 않는다. 담기/빼기는 syncFromChecked/removeFromLine 담당.
   */
  const readMerged = (): GrwLineRow[] => {
    const edited = new Map<string, any>();
    lineGridRef.current?.forEachNode?.((node: any) => {
      if (node?.data) edited.set(keyOf(node.data), node.data);
    });

    return lines.map((line) => {
      // 다른 탭 행은 손대지 않는다
      if (line.tp_line !== tab) return line;
      const row = edited.get(keyOf(line));
      if (!row) return line;
      // 고정 행은 순서칸을 고쳐도 1번 그대로 둔다
      const next = { ...line, ...row, tp_line: tab };
      return isFixed(line) ? { ...next, no_order: 1 } : next;
    });
  };

  /** 편집분을 반영하고 순번까지 정리한 목록 */
  const readLines = (): GrwLineRow[] => finalize(readMerged());

  /** 탭을 옮기기 전에 지금 탭 편집분을 보관한다 */
  const changeTab = (next: string) => {
    if (next === tab) return;
    setLines(readLines());
    setTab(next);
  };

  const searchEmp = async (cond: { cd_dept?: string; nm_emp?: string }) => {
    if (!onSearchEmp) return;
    const rows = await onSearchEmp(cond);
    setEmpRows(Array.isArray(rows) ? rows : []);
  };

  /**
   * 사원 목록의 체크 상태를 지금 탭에 반영한다.
   *
   * 체크하면 담기고 해제하면 빠진다. 단 **지금 사원 목록에 없는 행**(다른 부서에서
   * 담아둔 사람)은 체크로 판단할 수 없으므로 건드리지 않는다.
   */
  const syncFromChecked = () => {
    const checked: GrwEmpRow[] = empGridRef.current?.getCheckedRows?.() ?? [];
    const checkedKeys = new Set(checked.map(keyOf));
    const visibleKeys = new Set(empRows.map(keyOf));

    const current = readLines();

    // 지금 목록에 보이는 사람 중 체크 안 된 건 뺀다
    const kept = current.filter((l) => {
      if (l.tp_line !== tab) return true;
      if (!visibleKeys.has(keyOf(l))) return true;
      return checkedKeys.has(keyOf(l));
    });

    // 체크됐는데 아직 안 담긴 사람을 넣는다
    const existing = new Set(
      kept.filter((l) => l.tp_line === tab).map(keyOf)
    );
    // 새로 담는 사람은 맨 뒤에 붙는다 (renumber가 순서로 정렬하므로 큰 값을 준다)
    const added: GrwLineRow[] = checked
      .filter((e) => !existing.has(keyOf(e)))
      .map((e) => ({ ...e, tp_line: tab, no_order: Number.MAX_SAFE_INTEGER }));

    setLines(finalize([...kept, ...added]));
  };

  /** 결재경로에서 더블클릭 → 뺀다 (다른 부서에서 담아둔 사람을 지울 때) */
  const removeFromLine = (row: GrwLineRow) => {
    if (!row) return;
    // 기안자는 뺄 수 없다
    if (isFixed({ ...row, tp_line: tab })) return;

    setLines(
      finalize(
        readLines().filter(
          (l) => !(l.tp_line === tab && keyOf(l) === keyOf(row))
        )
      )
    );
  };

  /** 경로그룹 콤보를 다시 읽는다 (열 때 한 번, 추가·삭제 뒤에 한 번) */
  const loadGroups = async () => {
    if (!onLoadPathGroups) return;
    const rows = await onLoadPathGroups();
    setGroups(Array.isArray(rows) ? rows : []);
  };

  useEffect(() => {
    loadGroups();
    // 열릴 때 한 번만 — 이후 갱신은 추가·삭제가 직접 부른다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * 지금 담긴 결재경로를 이름 붙여 경로그룹으로 저장한다.
   * 저장은 화면 콜백에 맡기고, 성공하면 이름칸을 비우고 콤보를 다시 읽는다.
   */
  const addGroup = async () => {
    if (!onAddPathGroup) return;

    const name = groupName.trim();
    if (!name) {
      await showDialog("alter", "경로그룹명을 입력하세요.");
      return;
    }

    const picked = readLines();
    if (picked.length === 0) {
      await showDialog("alter", "결재경로에 담긴 사람이 없습니다.");
      return;
    }

    const ok = await onAddPathGroup(name, picked.map(toTemplateLine));
    if (ok === false) return;

    setGroupName("");
    await loadGroups();
  };

  const deleteGroup = async () => {
    if (!onDeletePathGroup) return;
    if (!cdGroup) {
      await showDialog("alter", "삭제할 경로그룹을 고르세요.");
      return;
    }

    const ok = await onDeletePathGroup(cdGroup);
    if (ok === false) return;

    setCdGroup("");
    await loadGroups();
  };

  /**
   * 경로그룹을 고르면 담겨 있던 결재경로를 그대로 불러온다 — 이 기능을 쓰는 이유.
   * 지금 담긴 사람은 통째로 그룹 내용으로 바뀐다.
   */
  const applyGroup = (code: string) => {
    setCdGroup(code);
    if (!code) return;

    const picked = groups.find((g) => g.code === code)?.lines;
    if (!Array.isArray(picked) || picked.length === 0) return;

    setLines(
      finalize(
        picked.map((l, i) => ({
          ...toTemplateLine(l),
          // 저장 당시 탭 구분이 비어 있으면 첫 탭으로 본다
          tp_line: l.tp_line || tabList[0],
          no_order: Number(l.no_order) || i + 1,
        }))
      )
    );
  };

  /**
   * 사원 목록의 체크 상태를 지금 탭 소속과 맞춘다.
   *
   * 체크는 탭을 옮겨도 그대로 남는다. 그냥 두면 이전 탭에서 체크해둔 사람들이
   * 계속 getCheckedRows()에 잡혀서, 새 탭에서 한 명만 체크해도 그 사람들까지
   * 전부 담긴다. 탭/사원목록/담긴 사람이 바뀔 때마다 체크를 실제 소속에 맞춘다.
   * noEvent로 넣어 syncFromChecked가 다시 돌지 않게 한다.
   */
  useEffect(() => {
    const grid = empGridRef.current;
    if (!grid?.forEachNode) return;

    const memberKeys = new Set(
      lines.filter((l) => l.tp_line === tab).map(keyOf)
    );
    grid.forEachNode((node: any) => {
      if (!node?.data) return;
      const shouldCheck = memberKeys.has(keyOf(node.data));
      if (!!node.data.fixCheckbox !== shouldCheck) {
        grid.setCheckedRow?.(node, shouldCheck, "noEvent");
      }
    });
  }, [tab, empRows, lines]);

  /**
   * 지금 탭에 담긴 결재경로.
   *
   * 반드시 memo로 잡아야 한다. 렌더 때마다 새로 filter하면 내용이 같아도 배열 참조가 바뀌고,
   * SnGrid는 rowData 참조가 바뀌면 새 조회로 보고 0행에 포커스를 준다(SnGrid.tsx의
   * memoizedRowData → isInitialFocusPending → selectAndFocusRow). 그러면 위 입력칸에
   * 글자를 칠 때마다 커서가 이 표로 끌려간다.
   */
  const tabRows = useMemo(
    () => lines.filter((l) => l.tp_line === tab),
    [lines, tab]
  );

  return (
    <div ref={rootRef} style={ROOT}>
      {/* 사원검색 / 경로그룹 */}
      <div style={COND}>
        <span style={LABEL}>사원검색</span>
        <TextField
          value={empKeyword}
          onChange={(e) => setEmpKeyword(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") searchEmp({ nm_emp: empKeyword.trim() });
          }}
          fullWidth
          sx={FIELD_SX}
        />
        <Button
          type="button"
          variant="outlined"
          size="small"
          startIcon={<SearchIcon />}
          onClick={() => searchEmp({ nm_emp: empKeyword.trim() })}
        >
          검색
        </Button>
        <span />

        <span style={LABEL}>경로그룹추가</span>
        <TextField
          value={groupName}
          onChange={(e) => setGroupName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") addGroup();
          }}
          fullWidth
          sx={FIELD_SX}
        />
        <Button type="button" variant="outlined" size="small" onClick={addGroup}>
          추가
        </Button>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={LABEL}>경로그룹</span>
          <select
            value={cdGroup}
            onChange={(e) => applyGroup(e.target.value)}
            style={SELECT}
          >
            <option value="" />
            {groups.map((g) => (
              <option key={g.code} value={g.code}>
                {g.name}
              </option>
            ))}
          </select>
          <Button
            type="button"
            variant="outlined"
            size="small"
            onClick={deleteGroup}
          >
            삭제
          </Button>
        </div>
      </div>

      {/* 부서(좌) + 사원·결재경로(우) */}
      <div style={BODY}>
        <div style={TREE_BOX}>
          {depts.map((d) => (
            <div
              key={d.cd_dept || d.nm_dept}
              style={{
                ...TREE_ITEM,
                ...(cdDept === d.cd_dept ? TREE_ITEM_ON : null),
              }}
              onClick={() => {
                setCdDept(d.cd_dept);
                searchEmp({ cd_dept: d.cd_dept });
              }}
            >
              <span style={{ marginRight: 6 }}>📁</span>
              {d.nm_dept}
            </div>
          ))}
          {depts.length === 0 && (
            <div style={{ padding: 8, fontSize: 12, color: "#888" }}>
              부서 목록이 없습니다.
            </div>
          )}
        </div>

        <div style={RIGHT}>
          {/* 사원 목록 */}
          <div style={{ flex: 1, minHeight: 0 }}>
            <SnGrid
              ref={empGridRef}
              columnConfig={EMP_COLUMNS}
              rowData={empRows}
              etcProps={{
                rowSelection: "multiple",
                isEditable: false,
                ischeckboxSelection: true,
                popupParent: popupParentEl,
              }}
              events={{
                // 체크 값이 그리드에 반영된 뒤 읽어야 해서 한 틱 미룬다
                onCheckedRowChaged: () => setTimeout(syncFromChecked, 0),
              }}
            />
          </div>

          {/* 탭 */}
          <div style={{ display: "flex", gap: 2, flex: "0 0 auto" }}>
            {tabList.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => changeTab(t)}
                style={TAB_BTN(tab === t)}
              >
                {t}
              </button>
            ))}
          </div>

          {/* 결재경로 */}
          <div style={{ flex: 1, minHeight: 0 }}>
            <SnGrid
              // 탭이 바뀌면 표를 새로 그린다
              key={tab}
              ref={lineGridRef}
              columnConfig={LINE_COLUMNS}
              rowData={tabRows}
              etcProps={{
                rowSelection: "single",
                isEditable: true,
                ischeckboxSelection: false,
                popupParent: popupParentEl,
              }}
              events={{
                /**
                 * 더블클릭으로 뺀다 — 단 결재순서 칸은 뺀다.
                 * 그 칸의 더블클릭은 "편집 시작"이라 여기서 지우면 순서를 못 고친다.
                 */
                onCellDoubleClicked: (e: any) => {
                  if (e?.column?.getColId?.() === "no_order") return;
                  removeFromLine(e?.data);
                },
                onCellValueChanged: (e: any) => {
                  if (e?.colId !== "no_order") return;

                  // 기안자 줄은 순서를 바꿀 수 없다
                  if (isFixed({ ...e.rowData, tp_line: tab })) {
                    if (Number(e.rowData?.no_order) !== 1) {
                      e.rowNode?.setDataValue?.("no_order", 1, "noEvent");
                    }
                    return;
                  }

                  // 방금 친 숫자대로 줄을 다시 세운다 (번호는 그대로 둔다)
                  setLines(arrange(readMerged()));
                },
              }}
            />
          </div>

          <div style={{ fontSize: 11, color: "#888", flex: "0 0 auto" }}>
            사원 목록에서 체크하면 "{tab}"에 담기고, 해제하면 빠집니다. 다른 부서에서
            담아둔 사람은 아래 표의 이름칸을 더블클릭해 빼세요. 결재순서는 숫자를 고쳐
            바꿀 수 있습니다{fixedLine ? ` — 다만 기안자(${fixedLine.nm_emp})는 항상 1번이라 빼거나 순서를 바꿀 수 없습니다` : ""}.
            자주 쓰는 결재경로는 이름을 적고 [추가]로 묶어두면, 다음부터는 위
            경로그룹에서 고르기만 하면 됩니다.
          </div>
        </div>
      </div>
    </div>
  );
});

const ROOT: React.CSSProperties = {
  position: "relative",
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minHeight: 0,
  gap: 8,
};

const COND: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "auto minmax(160px, 1fr) auto minmax(240px, 1fr)",
  alignItems: "center",
  gap: "6px 8px",
  flex: "0 0 auto",
};

const BODY: React.CSSProperties = {
  display: "flex",
  gap: 8,
  flex: 1,
  minHeight: 0,
};

const TREE_BOX: React.CSSProperties = {
  width: 200,
  flex: "0 0 auto",
  border: "1px solid rgb(196 196 196)",
  borderRadius: 4,
  overflow: "auto",
  background: "#fff",
};

const TREE_ITEM: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  padding: "4px 8px",
  fontSize: StyleSettings.font,
  cursor: "pointer",
  whiteSpace: "nowrap",
};

const TREE_ITEM_ON: React.CSSProperties = {
  background: "#dbeafe",
};

const RIGHT: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  gap: 6,
};

const LABEL: React.CSSProperties = {
  fontSize: StyleSettings.font,
  whiteSpace: "nowrap",
};

const FIELD_SX = {
  "& .MuiInputBase-root": { height: StyleSettings.control_height },
  "& .MuiInputBase-input": { fontSize: StyleSettings.font },
};

const SELECT: React.CSSProperties = {
  height: StyleSettings.control_height,
  minWidth: 160,
  fontSize: StyleSettings.font,
  border: "1px solid rgb(196 196 196)",
  borderRadius: 4,
  background: "#fff",
};

const TAB_BTN = (active: boolean): React.CSSProperties => ({
  padding: "3px 16px",
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
  border: "1px solid rgb(186,191,199)",
  borderBottomColor: active ? "#fff" : "rgb(186,191,199)",
  borderTopLeftRadius: 4,
  borderTopRightRadius: 4,
  marginBottom: -1,
  background: active ? "#fff" : "#f2f4f7",
  color: active ? "#0b0c69" : "#555",
});

export default GrwApprovalLinePopup;
